/**
 * Radiocarbon (¹⁴C) age arithmetic.
 *
 * ## Conventions (Stuiver & Polach, "Discussion: Reporting of ¹⁴C data", Radiocarbon 19(3),
 * 355–363, 1977)
 *
 * - A **conventional radiocarbon age** is t = −8033 · ln(A_SN / A_ON), using the *Libby*
 *   half-life of 5,568 y (mean life 5,568 / ln 2 ≈ 8,033 y) even though the true half-life
 *   is closer to 5,730 y (the 1962 "Cambridge" value, ± 40 y). Laboratories kept Libby's
 *   value so that dates stay comparable with the thousands already published, and the
 *   calibration curves are built on it.
 * - A_SN is the sample activity normalised to δ¹³C = −25 ‰ (VPDB):
 *   A_SN = A_S · [1 − 2(25 + δ¹³C)/1000].
 * - A_ON is the modern reference: 0.95 × the AD 1950 activity of NBS oxalic acid I. Both
 *   sides are decay-corrected to 1950, so ages are counted back from 1950 ("BP" = before
 *   present, "present" fixed at AD 1950).
 * - The ratio A_SN / A_ON is the **fraction modern** F¹⁴C (Reimer, Brown & Reimer, Radiocarbon
 *   46(3), 1299–1304, 2004); percent modern carbon (pMC) is 100 · F¹⁴C.
 *
 * Nothing here calibrates. A conventional age is in "¹⁴C years BP"; turning it into
 * calendar years needs a calibration curve (IntCal20, Reimer et al., Radiocarbon 62(4),
 * 725–757, 2020) and a calibration program.
 */

export const LIBBY_HALF_LIFE = 5568;
/** The mean life used for conventional ages: 5,568 / ln 2 = 8,033 (rounded as published). */
export const LIBBY_MEAN_LIFE = 8033;
export const CAMBRIDGE_HALF_LIFE = 5730;
export const CAMBRIDGE_MEAN_LIFE = CAMBRIDGE_HALF_LIFE / Math.LN2;
/** Age at which F¹⁴C ≈ 0.2 %, the usual practical limit of the method. */
export const PRACTICAL_LIMIT_YEARS = 50_000;

export type InputMode = "pmc" | "f14c" | "ratio";

export interface FractionInput {
  mode: InputMode;
  /** pMC, F¹⁴C, or the sample ¹⁴C/¹²C ratio, depending on `mode`. */
  value: number;
  /** 1σ uncertainty in the same unit as `value`; 0 or null for none. */
  sigma: number | null;
  /** Reference ¹⁴C/¹²C ratio (ratio mode only). */
  reference: number | null;
  /** δ¹³C in ‰ VPDB; null for no fractionation correction. */
  delta13c: number | null;
}

export type RadiocarbonError =
  | "value" // not positive
  | "reference" // ratio mode with a missing or non-positive reference
  | "sigma" // negative uncertainty
  | "fractionation"; // δ¹³C outside a physical range

export interface Fraction {
  /** Fraction modern F¹⁴C after any δ¹³C normalisation. */
  f14c: number;
  /** 1σ on F¹⁴C, from the entered uncertainty (0 if none). */
  sigma: number;
  /** The factor applied for δ¹³C; 1 when none. */
  fractionationFactor: number;
}

/** δ¹³C beyond ±100 ‰ of the −25 ‰ reference is not a plausible material. */
const MAX_DELTA = 100;

/** A_SN / A_S for a δ¹³C in ‰: 1 − 2(25 + δ)/1000. */
export function fractionationFactor(delta13c: number): number {
  return 1 - (2 * (25 + delta13c)) / 1000;
}

export function fractionFromInput(
  input: FractionInput
): Fraction | { error: RadiocarbonError } {
  const { mode, value, sigma, reference, delta13c } = input;
  if (!(Number.isFinite(value) && value > 0)) {
    return { error: "value" };
  }
  if (sigma !== null && !(Number.isFinite(sigma) && sigma >= 0)) {
    return { error: "sigma" };
  }
  if (delta13c !== null && !(Math.abs(delta13c) <= MAX_DELTA)) {
    return { error: "fractionation" };
  }
  let scale = 1;
  if (mode === "pmc") {
    scale = 1 / 100;
  } else if (mode === "ratio") {
    if (reference === null || !(Number.isFinite(reference) && reference > 0)) {
      return { error: "reference" };
    }
    scale = 1 / reference;
  }
  const factor = delta13c === null ? 1 : fractionationFactor(delta13c);
  return {
    f14c: value * scale * factor,
    sigma: (sigma ?? 0) * scale * factor,
    fractionationFactor: factor,
  };
}

/** Age in years from F¹⁴C for a mean life: t = −τ ln F. */
export function ageFromFraction(f14c: number, meanLife: number): number {
  return -meanLife * Math.log(f14c);
}

/** 1σ age uncertainty by first-order propagation: σ_t = τ σ_F / F. */
export function ageSigma(
  f14c: number,
  sigmaF: number,
  meanLife: number
): number {
  return (meanLife * sigmaF) / f14c;
}

/** F¹⁴C from an age: F = exp(−t / τ). */
export function fractionFromAge(age: number, meanLife: number): number {
  return Math.exp(-age / meanLife);
}

/** The factor that turns a Libby-basis age into a Cambridge-basis age: 5730 / 5568. */
export const CAMBRIDGE_OVER_LIBBY = CAMBRIDGE_HALF_LIFE / LIBBY_HALF_LIFE;

export interface AgeResult {
  f14c: number;
  pmc: number;
  /** Conventional (Libby) age, ¹⁴C yr BP. */
  libbyAge: number;
  libbySigma: number;
  /** The same fraction on the Cambridge half-life. */
  cambridgeAge: number;
  cambridgeSigma: number;
  /** True when F¹⁴C ≥ 1: modern or post-bomb carbon, no age to report. */
  modern: boolean;
  /** True when the Libby age exceeds the practical limit of the method. */
  beyondLimit: boolean;
}

/** Everything an age report needs from a fraction modern. */
export function describeFraction(fraction: Fraction): AgeResult {
  const { f14c, sigma } = fraction;
  const modern = f14c >= 1;
  const libbyAge = modern ? 0 : ageFromFraction(f14c, LIBBY_MEAN_LIFE);
  const cambridgeAge = modern ? 0 : ageFromFraction(f14c, CAMBRIDGE_MEAN_LIFE);
  return {
    f14c,
    pmc: f14c * 100,
    libbyAge,
    libbySigma: ageSigma(f14c, sigma, LIBBY_MEAN_LIFE),
    cambridgeAge,
    cambridgeSigma: ageSigma(f14c, sigma, CAMBRIDGE_MEAN_LIFE),
    modern,
    beyondLimit: libbyAge > PRACTICAL_LIMIT_YEARS,
  };
}

export interface ForwardResult {
  f14c: number;
  pmc: number;
  pmcSigma: number;
}

/** From a conventional age (and optional 1σ) to the fraction modern it implies. */
export function fractionFromConventionalAge(
  age: number,
  sigma: number | null
): ForwardResult | { error: "age" | "sigma" } {
  if (!Number.isFinite(age) || age < 0) {
    return { error: "age" };
  }
  if (sigma !== null && !(Number.isFinite(sigma) && sigma >= 0)) {
    return { error: "sigma" };
  }
  const f14c = fractionFromAge(age, LIBBY_MEAN_LIFE);
  return {
    f14c,
    pmc: f14c * 100,
    pmcSigma: (f14c * (sigma ?? 0) * 100) / LIBBY_MEAN_LIFE,
  };
}

/** Reference rows: pMC against the age on each half-life. */
export const REFERENCE_PMC = [100, 75, 50, 25, 12.5, 6.25, 1, 0.2] as const;

export function referenceRows(): {
  pmc: number;
  libby: number;
  cambridge: number;
}[] {
  return REFERENCE_PMC.map((pmc) => ({
    pmc,
    libby: ageFromFraction(pmc / 100, LIBBY_MEAN_LIFE) || 0,
    cambridge: ageFromFraction(pmc / 100, CAMBRIDGE_MEAN_LIFE) || 0,
  }));
}
